import { useEffect, useState, type ReactNode } from "react";
import { NavLink } from "react-router";
import { db } from "../lib/db";
import { useConflictNotes, useContracts, useOnline, useSession } from "../lib/session";
import { applyUpdate, useUpdateAvailable } from "../lib/update";
import { Alert, AlertAction, cx, focusRing } from "./ui";

interface InstallEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

/** Journey step 5: offer to install after the third contract. */
function InstallPrompt() {
  const contracts = useContracts();
  const [event, setEvent] = useState<InstallEvent | null>(null);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem("ck-install-dismissed") === "1";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setEvent(e as InstallEvent);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);
  if (!event || dismissed || (contracts?.length ?? 0) < 3) return null;
  const dismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem("ck-install-dismissed", "1");
    } catch {
      // Private mode: it'll just ask again next visit.
    }
  };
  return (
    <Alert tone="info" onDismiss={dismiss}>
      Install ClauseKeep on this device for quick access and reminder notifications.{" "}
      <AlertAction
        onClick={async () => {
          await event.prompt();
          await event.userChoice;
          dismiss();
        }}
      >
        Install
      </AlertAction>
    </Alert>
  );
}

/** A new deploy is installed but the old version is still running. */
function UpdatePrompt() {
  const available = useUpdateAvailable();
  const [applying, setApplying] = useState(false);
  if (!available) return null;
  return (
    <Alert tone="info">
      A new version of ClauseKeep is ready.{" "}
      <AlertAction
        disabled={applying}
        onClick={() => {
          setApplying(true);
          applyUpdate();
        }}
      >
        {applying ? "Reloading…" : "Reload"}
      </AlertAction>
    </Alert>
  );
}

function SyncBadge() {
  const { sync } = useSession();
  const online = useOnline();
  const [tone, text, title] = ((): ["good" | "warn" | "bad" | "idle", string, string?] => {
    if (!online || sync.state === "offline") return ["warn", `Offline${sync.pending ? ` · ${sync.pending} change${sync.pending === 1 ? "" : "s"} waiting` : ""}`];
    if (sync.state === "syncing") return ["idle", "Syncing…"];
    if (sync.state === "error") return ["bad", "Sync problem", sync.error ?? ""];
    if (sync.pending) return ["idle", `${sync.pending} waiting`];
    return ["good", "Saved"];
  })();
  const dot = { good: "bg-good", warn: "bg-warn", bad: "bg-bad", idle: "bg-faint animate-pulse" }[tone];
  const color = { good: "text-good", warn: "text-warn", bad: "text-bad", idle: "text-faint" }[tone];
  return (
    <span className={cx("inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-medium", color)} title={title}>
      <span className={cx("h-1.5 w-1.5 rounded-full", dot)} aria-hidden />
      {text}
    </span>
  );
}

const icon = (d: string) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="hidden h-4 w-4 sm:block" aria-hidden>
    <path d={d} />
  </svg>
);

const links = [
  { to: "/", label: "Dashboard", end: true, icon: icon("M3 12 12 4l9 8M5 10v10h5v-6h4v6h5V10") },
  { to: "/contracts", label: "Contracts", end: false, icon: icon("M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5Zm0 0v5h5M9 13h6M9 17h4") },
  {
    to: "/settings",
    label: "Settings",
    end: false,
    icon: icon("M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm7.4-3a7.4 7.4 0 0 0-.1-1.3l2-1.6-2-3.4-2.4 1a7.5 7.5 0 0 0-2.2-1.3L14.4 3h-4l-.4 2.4a7.5 7.5 0 0 0-2.2 1.3l-2.4-1-2 3.4 2 1.6a7.4 7.4 0 0 0 0 2.6l-2 1.6 2 3.4 2.4-1a7.5 7.5 0 0 0 2.2 1.3l.4 2.4h4l.4-2.4a7.5 7.5 0 0 0 2.2-1.3l2.4 1 2-3.4-2-1.6c.1-.4.1-.9.1-1.3Z"),
  },
];

// Phones have no room for a fourth link; Settings links to Admin there instead.
const adminLink = { to: "/admin", label: "Admin", end: false, icon: icon("M4 20V10m6 10V4m6 16v-7m4 7H2"), wide: true };
const inboxIcon = icon("M22 12h-6l-2 3h-4l-2-3H2m20 0v6a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-6m20 0-3.5-7h-13L2 12");

export function Layout({ children }: { children: ReactNode }) {
  const { me } = useSession();
  const waiting = me?.inbox?.waiting ?? 0;
  // On phones the Inbox link shows only while something waits; the dashboard links to it too.
  const inbox = me?.inbox?.enabled ? [{ to: "/inbox", label: "Inbox", end: false, icon: inboxIcon, count: waiting, ...(waiting ? {} : { wide: true }) }] : [];
  // Phones fit three links: while the Inbox shows there, the logo is the way to the dashboard.
  const home = waiting && inbox.length ? [{ ...links[0]!, wide: true }] : links.slice(0, 1);
  const nav = [...home, links[1]!, ...inbox, ...links.slice(2), ...(me?.operator ? [adminLink] : [])];
  const notes = useConflictNotes();
  const online = useOnline();
  return (
    <div className="min-h-dvh">
      <header className="sticky top-0 z-10 border-b border-line bg-surface/85 backdrop-blur-md supports-[backdrop-filter]:bg-surface/75">
        <div className="mx-auto flex h-14 max-w-5xl items-center gap-3 px-4 sm:gap-6">
          <NavLink to="/" className={cx("flex shrink-0 items-center gap-2 rounded-lg font-semibold tracking-tight text-fg", focusRing)}>
            <img src="/icons/icon.svg" alt="" className="h-7 w-7" />
            <span className="hidden sm:inline">ClauseKeep</span>
          </NavLink>
          <nav className="flex gap-0.5" aria-label="Main">
            {nav.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                className={({ isActive }) =>
                  cx(
                    "flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-sm font-medium transition-colors sm:px-3",
                    "wide" in l && "max-sm:hidden",
                    focusRing,
                    isActive ? "bg-accent-soft text-accent-soft-fg" : "text-muted hover:bg-subtle hover:text-fg",
                  )
                }
              >
                {l.icon}
                {l.label}
                {"count" in l && typeof l.count === "number" && l.count > 0 && (
                  <span className="rounded-full bg-accent px-1.5 text-xs font-semibold tabular-nums text-on-accent" aria-label={`${l.count} waiting`}>
                    {l.count}
                  </span>
                )}
              </NavLink>
            ))}
          </nav>
          <div className="ml-auto">
            <SyncBadge />
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-5xl space-y-4 px-4 py-6 sm:py-8">
        <UpdatePrompt />
        {me?.org.suspended && (
          <Alert tone="bad">This workspace is suspended, so nothing can be changed or uploaded. You can still view and export your contracts. Contact support to restore it.</Alert>
        )}
        {!online && <Alert tone="warn">You're offline. You can view and edit contracts; changes sync when you're back online.</Alert>}
        {notes.map((n) => (
          <Alert key={n.id} tone="warn" onDismiss={() => void db.conflicts.delete(n.id!)}>
            {n.message}
          </Alert>
        ))}
        <InstallPrompt />
        {children}
      </main>
    </div>
  );
}
